import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { fetchGet } from '../../api/common';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  LineController,
  ScatterController,
  SubTitle,
} from 'chart.js';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Profile } from '@par/shared/types/player';
import { roundToOneDecimal, utcToLocalDate } from '../room/utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import { AdvancedStatsInfoDialog } from '../../dialog/AdvancedStatsInfoDialog';
import { useParams } from 'react-router-dom';
import { fetchProfileById } from '@par/shared/components/profile';

const colorRange = [
  '#FF4500', // Orange Red
  '#FF6347', // Tomato
  '#FF7F50', // Coral
  '#FFA500', // Orange
  '#FFD700', // Gold
  '#F0E68C', // Khaki
  '#ADFF2F', // Green-Yellow
  '#7FFF00', // Chartreuse
  '#7CFC00', // Lawn Green
  '#32CD32', // Lime Green
  '#20B2AA', // Light Sea Green
  '#48D1CC', // Medium Turquoise
  '#40E0D0', // Turquoise
  '#00CED1', // Dark Turquoise
  '#1E90FF', // Dodger Blue
  '#4169E1', // Royal Blue
  '#6A5ACD', // Slate Blue
  '#800080', // Purple
  '#9932CC', // Dark Orchid
  '#FF00FF', // Fuchsia / Magenta
];

const chartInitialOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
      position: 'top' as const,
      labels: {
        color: 'white',
        font: {
          size: 18,
        },
      },
    },
    title: {
      display: true,
      color: 'white',
      text: 'Average Score By Category',
      font: {
        size: 24,
      },
    },
    subtitle: {
      display: true,
      color: 'white',
      text: 'Click a bar to drill down',
      font: {
        size: 14,
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Categories (question count)',
        color: 'white',
        font: {
          size: 18,
        },
      },
      ticks: {
        color: colorRange,
      },
    },
    y: {
      min: -6,
      max: 6,
      title: {
        display: true,
        text: 'Average Score',
        color: 'white',
        font: {
          size: 18,
        },
      },
      ticks: {
        color: 'white',
      },
    },
  },
};

type CategoryStat = {
  averagePer50: number;
  reached50At: number;
  count: number;
};

type AdvancedStats = {
  'Art & Architecture': CategoryStat[];
  Business: CategoryStat[];
  'Classical Music': CategoryStat[];
  Culture: CategoryStat[];
  Film: CategoryStat[];
  'Food & Drink': CategoryStat[];
  Geography: CategoryStat[];
  History: CategoryStat[];
  Literature: CategoryStat[];
  Math: CategoryStat[];
  Niche: CategoryStat[];
  'Philosophy & Religion': CategoryStat[];
  'Politics & Civics': CategoryStat[];
  'Popular Music': CategoryStat[];
  Science: CategoryStat[];
  Sports: CategoryStat[];
  Theater: CategoryStat[];
  TV: CategoryStat[];
  'Video Games': CategoryStat[];
  'Vocab & Language': CategoryStat[];
};

export const ProfileAdvancedStats = () => {
  const theme = useTheme();
  const { profileId } = useParams();
  const [viewProfile, setViewProfile] = useState<Profile>();
  const [chartKey, setChartKey] = useState(0);
  const chartRef = useRef<any>();
  const [prevChartData, setPrevChartData] = useState<any>();
  const [prevChartOptions, setPrevChartOptions] =
    useState<any>(chartInitialOptions);
  const [prevChartCategories, setPrevChartCategories] = useState<string[]>();
  const [batchData, setBatchData] = useState<any>();
  const [chartData, setChartData] = useState<any>();
  const [chartOptions, setChartOptions] = useState<any>(chartInitialOptions);
  const [chartCategories, setChartCategories] = useState<string[]>();
  const [globalAverages, setGlobalAverages] = useState<{
    mmr800: Record<string, number>;
    mmr1000: Record<string, number>;
    mmr1200: Record<string, number>;
    mmr1400: Record<string, number>;
    mmr1600: Record<string, number>;
    mmr1800: Record<string, number>;
    mmr2000: Record<string, number>;
  }>();
  const [advancedStats, setAdvancedStats] =
    useState<Record<string, CategoryStat[]>>();
  const [showCategorySpecificChart, setShowCategorySpecificChart] =
    useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [inProgressToggle, setInProgressToggle] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>();
  const [showGlobalAverages800, setShowGlobalAverages800] = useState(false);
  const [showGlobalAverages1000, setShowGlobalAverages1000] = useState(false);
  const [showGlobalAverages1200, setShowGlobalAverages1200] = useState(false);
  const [showGlobalAverages1400, setShowGlobalAverages1400] = useState(false);
  const [showGlobalAverages1600, setShowGlobalAverages1600] = useState(false);
  const [showGlobalAverages1800, setShowGlobalAverages1800] = useState(false);
  const [showGlobalAverages2000, setShowGlobalAverages2000] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    fetchProfilePageData(profileId);
  }, [profileId]);

  useEffect(() => {
    if (!viewProfile) return;
    fetchAdvancedStats(profileId);
  }, [viewProfile]);

  const fetchProfilePageData = async (profileId: string) => {
    const profileRes: Profile = await fetchProfileById(profileId);
    setViewProfile(profileRes);
  };

  const fetchAdvancedStats = async (profileId: string) => {
    const res = await fetchGet(`/advanced-stats/user/${profileId}`);
    const resMetadata = await fetchGet(`/advanced-stats/metadata/`);
    const categories = getCategoryLabels(res);
    setChartCategories(categories);
    setAdvancedStats(res);
    const batch = getBatch(res);
    setBatchData(batch);
    const globalAverages800 = calculateGlobalAverages(resMetadata, 800);
    const globalAverages1000 = calculateGlobalAverages(resMetadata, 1000);
    const globalAverages1200 = calculateGlobalAverages(resMetadata, 1200);
    const globalAverages1400 = calculateGlobalAverages(resMetadata, 1400);
    const globalAverages1600 = calculateGlobalAverages(resMetadata, 1600);
    const globalAverages1800 = calculateGlobalAverages(resMetadata, 1800);
    const globalAverages2000 = calculateGlobalAverages(resMetadata, 2000);
    setGlobalAverages({
      mmr800: globalAverages800,
      mmr1000: globalAverages1000,
      mmr1200: globalAverages1200,
      mmr1400: globalAverages1400,
      mmr1600: globalAverages1600,
      mmr1800: globalAverages1800,
      mmr2000: globalAverages2000,
    });
    const data = {
      labels: categories,
      datasets: [
        {
          data: batch,
          backgroundColor: rgbColorRange(),
        },
      ],
    };
    setChartData(data);
    setPrevChartData(data);
  };

  const calculateGlobalAverages = (metadata: any[], mmr: number) => {
    const categories = {
      'Art & Architecture': null,
      Business: null,
      'Classical Music': null,
      Culture: null,
      Film: null,
      'Food & Drink': null,
      Geography: null,
      History: null,
      Literature: null,
      Math: null,
      Niche: null,
      'Philosophy & Religion': null,
      'Politics & Civics': null,
      'Popular Music': null,
      Science: null,
      Sports: null,
      Theater: null,
      TV: null,
      'Video Games': null,
      'Vocab & Language': null,
    };
    const catData1 = metadata.find(
      (m) => m.mmrBasePer100 === mmr
    ).categoryAverages;
    const catData2 = metadata.find(
      (m) => m.mmrBasePer100 === mmr + 100
    ).categoryAverages;
    for (const cat of Object.keys(categories)) {
      const average = (catData1[cat] + catData2[cat]) / 2;
      categories[cat] = average;
    }
    return categories;
  };

  const getCategoryLabels = (
    categoryStats: Record<string, CategoryStat[]>,
    withCount?: boolean
  ) => {
    if (withCount) {
      const categories = Object.keys(categoryStats).map(
        (c) =>
          `${c} (${
            categoryStats[c].length
              ? categoryStats[c][categoryStats[c].length - 1].count
              : 0
          })`
      );
      return categories;
    }
    // const categories = Object.keys(categoryStats).map((c) => `${c} (50)`);
    const categories = Object.keys(categoryStats);
    return categories;
  };

  const getBatch = (categoryStats: Record<string, CategoryStat[]>) => {
    const batch = [];
    for (const category of Object.values(categoryStats)) {
      if (category.length > 1) {
        const averagePer50_1 = category[category.length - 1].averagePer50;
        const count_1 = category[category.length - 1].count;
        const averagePer50_2 = category[category.length - 2].averagePer50;
        const count_2 = category[category.length - 2].count;
        const weightedAvg =
          (averagePer50_1 * count_1 + averagePer50_2 * count_2) /
          (count_1 + count_2);
        batch.push(roundToOneDecimal(weightedAvg));
      } else {
        const averagePer50 = category[category.length - 1].averagePer50;
        batch.push(roundToOneDecimal(averagePer50));
      }
    }
    return batch;
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    LineController,
    ScatterController,
    Tooltip,
    Legend,
    Title,
    SubTitle
  );

  const handleCategoryClick = (event: any) => {
    if (showCategorySpecificChart) return;
    const { current: chart } = chartRef;
    if (!chart) return;
    const element = getElementAtEvent(chart, event);
    if (!element.length) return;
    const { index } = element[0];
    setSelectedCategoryIndex(index);
  };

  const updateCategoryOptions = (
    category: string,
    categoryTrendData: number[],
    categoryTrendDates: string[],
    newBgColor: string
  ) => {
    const categoryOptions = {
      responsive: true,
      plugins: {
        legend: {
          display: false,
          position: 'top' as const,
          labels: {
            color: 'white',
            font: {
              size: 18,
            },
          },
        },
        title: {
          display: true,
          color: 'white',
          text: category,
          font: {
            size: 24,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: newBgColor,
          },
        },
        y: {
          min: -6,
          max: 6,
          title: {
            display: true,
            text: 'Average Score',
            color: 'white',
            font: {
              size: 18,
            },
          },
          ticks: {
            color: 'white',
          },
        },
      },
    };
    setChartOptions(categoryOptions);

    const data = {
      labels: categoryTrendDates,
      datasets: [
        {
          type: 'line' as const,
          data: categoryTrendData,
          backgroundColor: 'white',
          borderColor: 'white',
          borderWidth: 2,
          fill: false,
        },
        {
          data: categoryTrendData,
          backgroundColor: newBgColor,
        },
      ],
    };
    setChartData(data);
    setShowCategorySpecificChart(true);
  };

  useEffect(() => {
    if (selectedCategoryIndex === undefined) return;
    if (!chartCategories || !advancedStats) return;
    const categoryWithCount = chartCategories[selectedCategoryIndex];
    const parenIndex = categoryWithCount.indexOf('(') - 1;
    const category =
      parenIndex > 0
        ? categoryWithCount.slice(0, parenIndex)
        : categoryWithCount;
    const categoryTrend = advancedStats[category];
    const categoryTrendData = categoryTrend.map((c) => c.averagePer50);
    const categoryTrendDates = categoryTrend.map((c) =>
      c.reached50At ? utcToLocalDate(c.reached50At) : `in-progress: ${c.count}`
    );
    updateCategoryOptions(
      category,
      categoryTrendData,
      categoryTrendDates,
      colorRange[selectedCategoryIndex]
    );
  }, [selectedCategoryIndex]);

  const handleViewAllClick = () => {
    setSelectedCategoryIndex(undefined);
    setShowCategorySpecificChart(false);
    setChartData(prevChartData);
    setChartOptions(prevChartOptions);
    setChartKey((prev) => prev + 1);
  };

  const rgbColorRange = (opacity?: number) =>
    colorRange.map((hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgb(${r}, ${g}, ${b}, ${opacity || 1})`;
    });

  const handleGlobalAveragesCheck = (e: any, mmr: number) => {
    const checked = e.target.checked;
    if (mmr === 800) {
      setShowGlobalAverages800(checked);
    } else if (mmr === 1000) {
      setShowGlobalAverages1000(checked);
    } else if (mmr === 1200) {
      setShowGlobalAverages1200(checked);
    } else if (mmr === 1400) {
      setShowGlobalAverages1400(checked);
    } else if (mmr === 1600) {
      setShowGlobalAverages1600(checked);
    } else if (mmr === 1800) {
      setShowGlobalAverages1800(checked);
    } else if (mmr === 2000) {
      setShowGlobalAverages2000(checked);
    }
  };

  useEffect(() => {
    if (!batchData || showCategorySpecificChart) return;

    let batch = batchData;
    const categories = getCategoryLabels(advancedStats);
    setChartCategories(categories);
    setPrevChartCategories(categories);
    batch = getBatch(advancedStats);
    setBatchData(batch);

    let backgroundColors;
    if (
      showGlobalAverages800 ||
      showGlobalAverages1000 ||
      showGlobalAverages1200 ||
      showGlobalAverages1400 ||
      showGlobalAverages1600 ||
      showGlobalAverages1800 ||
      showGlobalAverages2000
    ) {
      const noAnimation = {
        ...chartOptions,
        animation: {
          duration: 0,
        },
      };
      setChartOptions(noAnimation);
      backgroundColors = rgbColorRange(0.5);
    } else {
      delete chartOptions.animation;
      setChartOptions(chartOptions);
      backgroundColors = rgbColorRange(1);
    }

    const dataset: any = [
      {
        data: batch,
        backgroundColor: backgroundColors,
      },
    ];

    const datasetObj = (
      globalAveragesDataset: any,
      borderColor: string,
      pointStyle: string
    ) => ({
      type: 'scatter' as const,
      data: Object.values(globalAveragesDataset),
      borderColor: 'white',
      borderWidth: 2,
      pointRadius: 8,
      pointHoverRadius: 20,
      pointStyle: pointStyle,
    });

    if (showGlobalAverages800) {
      dataset.push(datasetObj(globalAverages.mmr800, '#FF4500', 'rectRot'));
    }
    if (showGlobalAverages1000) {
      dataset.push(datasetObj(globalAverages.mmr1000, '#32CD32', 'cross'));
    }
    if (showGlobalAverages1200) {
      dataset.push(datasetObj(globalAverages.mmr1200, '#1E90FF', 'circle'));
    }
    if (showGlobalAverages1400) {
      dataset.push(datasetObj(globalAverages.mmr1400, '#FFD700', 'line'));
    }
    if (showGlobalAverages1600) {
      dataset.push(datasetObj(globalAverages.mmr1600, '#FF00FF', 'triangle'));
    }
    if (showGlobalAverages1800) {
      dataset.push(datasetObj(globalAverages.mmr1800, '#FF6347', 'star'));
    }
    if (showGlobalAverages2000) {
      dataset.push(
        datasetObj(globalAverages.mmr2000, '#40E0D0', 'rectRounded')
      );
    }

    const data = {
      labels: chartCategories,
      datasets: dataset,
    };

    setChartData(data);
    setChartKey((prev) => prev + 1);
  }, [
    showCategorySpecificChart,
    showGlobalAverages800,
    showGlobalAverages1000,
    showGlobalAverages1200,
    showGlobalAverages1400,
    showGlobalAverages1600,
    showGlobalAverages1800,
    showGlobalAverages2000,
  ]);

  return (
    <>
      <AdvancedStatsInfoDialog
        close={() => setOpenInfoDialog(false)}
        open={openInfoDialog}
      />
      <Container>
        <Typography variant="h4" textAlign={'center'} letterSpacing={20}>
          <Typography
            variant="h6"
            textAlign={'center'}
            letterSpacing={5}
            mb={1}
          >
            {viewProfile?.username}'s
          </Typography>
          ADVANCED STATS
        </Typography>
        <Stack sx={{ width: '100%', marginBottom: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'right' }}>
            <Button
              startIcon={<InfoIcon />}
              onClick={() => setOpenInfoDialog(true)}
              sx={{
                color: 'white',
              }}
            >
              How do advanced stats work?
            </Button>
          </Box>
        </Stack>
        <Box
          sx={{
            marginBottom: 2,
            paddingTop: 1,
            paddingX: 1,
            border: '1px solid',
            borderColor: theme.palette.ranked.main,
            borderRadius: '5px',
          }}
        >
          <Typography textAlign={'center'} fontWeight={600}>
            Global averages by MMR range
          </Typography>
          <Stack
            direction="row"
            spacing={4}
            sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            {/* <Stack direction="row" sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={showGlobalAverages800}
              onChange={(e) => handleGlobalAveragesCheck(e, 800)}
              disabled={showCategorySpecificChart}
            />
            <Typography>800-1000</Typography>
          </Stack> */}
            <Stack
              direction="row"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGlobalAverages1000}
                    onChange={(e) => handleGlobalAveragesCheck(e, 1000)}
                    disabled={showCategorySpecificChart}
                  />
                }
                label="1000-1200"
              />
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGlobalAverages1200}
                    onChange={(e) => handleGlobalAveragesCheck(e, 1200)}
                    disabled={showCategorySpecificChart}
                  />
                }
                label="1200-1400"
              />
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGlobalAverages1400}
                    onChange={(e) => handleGlobalAveragesCheck(e, 1400)}
                    disabled={showCategorySpecificChart}
                  />
                }
                label="1400-1600"
              />
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGlobalAverages1600}
                    onChange={(e) => handleGlobalAveragesCheck(e, 1600)}
                    disabled={showCategorySpecificChart}
                  />
                }
                label="1600-1800"
              />
            </Stack>
            <Stack
              direction="row"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGlobalAverages1800}
                    onChange={(e) => handleGlobalAveragesCheck(e, 1800)}
                    disabled={showCategorySpecificChart}
                  />
                }
                label="1800-2000"
              />
            </Stack>
            {/* <Stack direction="row" sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={showGlobalAverages2000}
              onChange={(e) => handleGlobalAveragesCheck(e, 2000)}
              disabled={showCategorySpecificChart}
            />
            <Typography>2000-2200</Typography>
          </Stack> */}
          </Stack>
        </Box>
        <Box
          sx={{
            position: 'relative',
            height: 1000,
            width: 1200,
          }}
        >
          <Stack direction={'row'}>
            <Box width={'25%'}>
              {showCategorySpecificChart ? (
                <Button
                  onClick={handleViewAllClick}
                  startIcon={<ArrowBackIcon />}
                >
                  Back to All Categories
                </Button>
              ) : null}
            </Box>
            <Box width={'50%'}>
              {/* <Stack
              direction="row"
              spacing={2}
              sx={{
                width: '100%',
                marginBottom: 2,
                color: 'dailies.main',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography>Current Batch</Typography>
              <Switch
                checked={inProgressToggle}
                onChange={() => setInProgressToggle((prev) => !prev)}
                disabled={showCategorySpecificChart}
              />
              <Typography>Previous Batch of 50</Typography>
            </Stack> */}
            </Box>
            <Box width={'25%'}></Box>
          </Stack>
          {chartOptions && chartData ? (
            <Bar
              ref={chartRef}
              key={chartKey}
              options={chartOptions}
              data={chartData}
              onClick={handleCategoryClick}
            />
          ) : null}
        </Box>
      </Container>
    </>
  );
};
